import {createContext, type Dispatch, use} from 'react'

import type {BoardFocusAction, BoardFocusGrid, BoardFocusState} from './types'

/**
 * Focus is published as three contexts, none of which changes when focus moves.
 *
 * That is the whole point of the shape. A board draws a card per item and a cell per pair,
 * and every one of them needs to know whether it is the roving item. Answering that
 * through a context value carrying the position means one arrow key invalidates every card
 * on the board: the cost of a keypress grows with what is on screen rather than with what
 * changed, and what changed is two elements. Measured on a sixty-by-twenty-four board, it
 * was every rendered card re-drawn three times per press.
 *
 * So the position lives in a store that components subscribe to for the one fact they care
 * about, through `useSyncExternalStore`. A move re-renders the two cards whose answer
 * changed and nothing else.
 *
 * `BoardFocusStoreContext` holds the store and the two ways to change it, and never
 * changes. `BoardFocusGridContext` changes only when the caller's data does.
 * `BoardPositionRenderedContext` changes when the element at the position mounts or
 * unmounts, and only the board's root reads it.
 */

export type BoardFocusStore = {
  dispatch: Dispatch<BoardFocusAction>
  /**
   * Read without subscribing. For somewhere that needs the position to answer an event it
   * is already handling, rather than to draw itself.
   */
  getState: () => BoardFocusState
  subscribe: (listener: () => void) => () => void
  /**
   * Called by whichever element is drawing the roving item, for as long as it is drawing
   * it. Returns the release. Counted rather than flagged, because a move hands the claim
   * from one element to another and React tears the old one down after building the new.
   */
  claimPosition: () => () => void
  /**
   * Said by an element at a position as focus arrives on it, and asked by the board's root
   * as that same event reaches it a moment later.
   *
   * The event itself rather than a flag, because identity is the question: a flag would
   * still be set from the last time focus moved. It is how the root tells focus landing on
   * a position from focus landing on a control the caller drew between them, and it is
   * asked of the position rather than worked out from the element, because only the
   * position knows: a card takes focus from anywhere inside itself and a cell body takes
   * only its own, and both are answered by the same handler that reports the arrival.
   */
  reportFocusAtPosition: (focusEvent: Event) => void
  isFocusAtPosition: (focusEvent: Event) => boolean
}

export const BoardFocusStoreContext = createContext<BoardFocusStore | undefined>(undefined)

/** The board the reducer moves over, rebuilt from the caller's data. */
export const BoardFocusGridContext = createContext<BoardFocusGrid | undefined>(undefined)

/**
 * Whether an element in the page is currently the roving item.
 *
 * The board's root is a tab stop only while this is false. Exactly one element is
 * reachable by tab, and it should be the roving item, but the position may name a column
 * the window has scrolled past, and a board with no tab stop at all is one a keyboard
 * cannot reach. So the root takes the stop back whenever nothing else has it.
 */
export const BoardPositionRenderedContext = createContext(false)

export function useIsBoardPositionRendered(): boolean {
  return use(BoardPositionRenderedContext)
}

export function useBoardFocusGrid(): BoardFocusGrid {
  const value = use(BoardFocusGridContext)
  if (!value) {
    throw new Error('This hook must be called inside a BoardProvider.')
  }

  return value
}

export function useBoardFocusStore(): BoardFocusStore {
  const value = use(BoardFocusStoreContext)
  if (!value) {
    throw new Error('This hook must be called inside a BoardProvider.')
  }

  return value
}
